import React, { useState, useEffect } from 'react';
import { Layout, Typography, Avatar, Dropdown, Switch, Space } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { usePageTitle } from '../../context/PageTitleContext';
import { useTheme } from '../../context/ThemeContext';
import { authService } from '../../services/api';
import './AppHeader.css';

const { Header } = Layout;
const { Title, Text } = Typography;

/**
 * App header component with page title support using PageTitleContext
 */
const AppHeader = () => {
    // Получаем данные из контекста PageTitleContext
    const { pageTitle, rightContent } = usePageTitle();
    const { darkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const currentUser = authService.getCurrentUser();

    useEffect(() => {
        if (pageTitle) {
            document.title = `${pageTitle} - ID`;
        }
    }, [pageTitle]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [menuCollapsed, setMenuCollapsed] = useState(false);

    // Функция для выхода из системы
    const handleLogout = async () => {
        await authService.logout(navigate);
    };

    // Меню для выпадающего списка
    const userMenu = {
        items: [
            {
                key: '1',
                label: (
                    <div style={{ padding: '4px 0' }}>
                        <Text strong>
                            {currentUser
                                ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
                                : 'Гость'}
                        </Text>

                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {currentUser?.email || ''}
                        </Text>
                    </div>
                ),
            },
            {
                key: '2',
                label: (
                    <Space>
                        <Text>Тёмная тема</Text>
                        <Switch
                            checked={darkMode}
                            onChange={toggleTheme}
                            size="small"
                        />
                    </Space>
                ),
            },
            {
                type: 'divider',
                key: 'divider',
                style: { display: currentUser ? 'block' : 'none' }
            },
            {
                key: '3',
                danger: true,
                icon: <LogoutOutlined />,
                label: 'Выйти',
                onClick: handleLogout,
                style: { display: currentUser ? 'block' : 'none' }
            },
        ],
    };

    // Слушаем изменения размера окна
    useEffect(() => {
        // Функция проверки размера окна
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        // Подписываемся на событие изменения размера окна
        window.addEventListener('resize', checkScreenSize);

        // Обратный вызов для очистки обработчика событий
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Определяем, показывать ли логотип
    const showLogo = !isMobile && !menuCollapsed;

    //Показываем инициалы на аватарке
    const getInitials = (user) => {
        if (!user?.first_name) return 'U'; // Если нет имени (маловероятно), ставим "U"
        const firstInitial = user.first_name.charAt(0).toUpperCase();
        const lastInitial = user.last_name ? user.last_name.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    return (
        <>
            {pageTitle && (
                <Helmet>
                    <title>{pageTitle}</title>
                </Helmet>
            )}
            <Header className="app-header">
                {/* Left corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="app-header__corner app-header__corner--left"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>
                {/* Logo Box - показываем только если не мобильное устройство и меню не свернуто */}
                {showLogo && (
                    <div className="app-header__extra-content_placement_left">
                        <svg
                            width={63}
                            height={32}
                            viewBox="0 0 63 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="app-header__logo"
                        >
                            <path d="M41.8242 23.841V8.00101H45.0642V23.841H41.8242Z" fill="white"/>
                            <path
                                d="M48.6914 23.841V8.00101H53.8754C56.4034 7.96901 58.4834 8.69701 60.1154 10.185C61.7474 11.657 62.5474 13.553 62.5154 15.873C62.5474 18.177 61.7394 20.089 60.0914 21.609C58.4434 23.129 56.3714 23.873 53.8754 23.841H48.6914ZM51.9314 20.913H53.8514C55.4514 20.913 56.7394 20.457 57.7154 19.545C58.6914 18.617 59.1794 17.393 59.1794 15.873C59.1794 14.337 58.6994 13.129 57.7394 12.249C56.7794 11.369 55.4834 10.929 53.8514 10.929H51.9314V20.913Z"
                                fill="white"
                            />
                            <path
                                d="M22.7097 0H9.29032C4.15942 0 0 4.15942 0 9.29032V22.7097C0 27.8406 4.15942 32 9.29032 32H22.7097C27.8406 32 32 27.8406 32 22.7097V9.29032C32 4.15942 27.8406 0 22.7097 0Z"
                                fill="#05D32A"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M16.0001 9.25H9.53858V6.25H16.0001C17.2827 6.25 18.8796 6.5524 20.182 7.62042C21.54 8.73402 22.3463 10.488 22.3463 12.9015C22.3463 15.1291 21.4875 16.7522 20.1322 17.771C18.847 18.737 17.2803 19.0379 16.0001 19.0379C14.747 19.0379 13.1749 18.7742 11.8786 17.8175C10.504 16.803 9.65397 15.1676 9.65397 12.9015H12.654C12.654 14.3445 13.1501 15.0273 13.6601 15.4037C14.2484 15.8379 15.0994 16.0379 16.0001 16.0379C16.8738 16.0379 17.7301 15.8236 18.3296 15.3729C18.8589 14.9751 19.3463 14.28 19.3463 12.9015C19.3463 11.1938 18.8064 10.372 18.2797 9.94018C17.6975 9.46276 16.8713 9.25 16.0001 9.25ZM10.2862 25.6803C10.1352 25.7874 9.98917 25.8909 9.84643 25.9884L8.15381 23.5116C8.27317 23.43 8.40407 23.3364 8.54732 23.2339C9.83669 22.3118 12.1263 20.6742 16.0001 20.6742C20.2933 20.6742 22.7859 22.6608 23.9352 23.5771L22.065 25.9229L22.0643 25.9223C21.0596 25.1213 19.2446 23.6742 16.0001 23.6742C13.1158 23.6742 11.531 24.7978 10.2862 25.6803Z"
                                fill="#1C1D20"
                            />
                        </svg>
                    </div>
                )}

                {/* Header content container with page title and right content */}
                <div className="app-header__content">
                    {pageTitle && (
                        <Title className="app-header__title" level={5}>
                            {pageTitle}
                        </Title>
                    )}

                    <div className="app-header__right-content">
                        {rightContent && (
                            <div className="app-header__right-actions">
                                {rightContent}
                            </div>
                        )}
                    </div>
                </div>
                {/* Аватарка пользователя с выпадающим меню */}
                <Dropdown menu={userMenu} placement="bottomRight" arrow>
                    <Avatar size={40} className={currentUser ? "app-header__avatar-user" : "app-header__avatar-guest"}>
                        {currentUser ? (
                                getInitials(currentUser) // Показываем инициалы
                        ) : (
                            <UserOutlined /> // Если не авторизован, показываем иконку
                        )}
                    </Avatar>
                </Dropdown>


                {/* Right corner SVG */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    className="app-header__corner app-header__corner--right"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0 32V0H32C24.5602 0 20.8403 0 17.7883 0.817781C9.50613 3.03699 3.03699 9.50614 0.81778 17.7883C0 20.8403 0 24.5602 0 32Z"
                        fill="currentColor"
                    />
                </svg>
            </Header>
        </>
    );
};

export default AppHeader;